import { ref, readonly } from 'vue';
import { defineStore } from 'pinia';

import { assertValue } from '@libs/utils';
import { HotelAmenityType } from '@libs/trip-planner-interfaces';

import type { HotelAmenityData } from '../models';
import { getHotelAmenitiesConfig } from '../models';

// NOTE: This store holds hotel amenities tags rendered sizes (icon + translation) as globally available singleton.
export const useHotelAmenitiesStore = defineStore('HotelAmenities', () => {
  const iconSize = 18;
  const iconAmenitySpacing = 8;
  const amenityAmenityXSpacing = 16;
  const sidePadding = 12;
  const canvas = document.createElement('canvas');
  const amenitiesWithSizes = ref<Record<HotelAmenityType, number | undefined>>(
    Object.fromEntries(
      Object.values(HotelAmenityType).map((amenity) => [amenity, undefined])
    ) as Record<HotelAmenityType, number | undefined>
  );

  function measureAmenities(): void {
    const config = getHotelAmenitiesConfig();
    const amenityTypes = Object.keys(config) as (keyof typeof config)[];
    amenityTypes.forEach((amenityType: HotelAmenityType) => {
      const size = getElementSize(config[amenityType]);
      amenitiesWithSizes.value[amenityType] = size;
    });
  }

  function getElementSize(amenity: HotelAmenityData): number {
    const ctx = canvas.getContext('2d');
    assertValue(ctx, 'HotelAmenities.vue > getElementSize > ctx');
    ctx.font = getComputedStyle(document.body).getPropertyValue(
      '--font-body-s-light'
    );
    // --font-body-s-light' is using font size s = 0.75rem
    // Safari and Chrome interpret rem units differently in canvas context:
    // Chrome converts 0.75rem to 12px, Safari converts it to 7.5px
    // This causes text measurements to differ, leading to incorrect row calculations
    // Force 12px to ensure consistent text measurements across all browsers
    ctx.font = ctx.font.replace(/[\d.]+px/, '12px');
    const textWidth = ctx.measureText(amenity.value).width;

    return (
      textWidth +
      iconSize +
      iconAmenitySpacing +
      amenityAmenityXSpacing +
      sidePadding
    );
  }

  return { measureAmenities, amenitiesWithSizes: readonly(amenitiesWithSizes) };
});
